// SidebarNavList — the per-workflow nav list in the middle of the rail. Purely presentational:
// it takes an already-permission-filtered item list and renders it. Extracted from Sidebar.tsx so
// that file stays a thin container.
import { NavLink } from 'react-router-dom';
import { paths } from '@/routes/paths';
import { ApprovalsBadge } from './SidebarStatusBadges';
import { type SidebarNavItem, navIsActive } from './sidebarNav';

export interface SidebarNavListProps {
  items: SidebarNavItem[];
  pathname: string;
  /** Accessible name for the <nav>, e.g. "PE navigation". */
  ariaLabel: string;
  /** Unseen-changes count for the Activity badge; 0 hides it. */
  changeCount: number;
  /** False when the Approvals badge is already rendered on the Admin Ops group. */
  showApprovalsBadge: boolean;
  onNavigate: () => void;
}

export default function SidebarNavList({
  items,
  pathname,
  ariaLabel,
  changeCount,
  showApprovalsBadge,
  onNavigate,
}: SidebarNavListProps) {
  return (
    <nav className="sidebar-nav" aria-label={ariaLabel}>
      {items.map((item) => {
        const active = navIsActive(pathname, item.to);
        const Icon = item.icon;
        const badge =
          item.activityBadge && changeCount > 0 ? (
            <span className="sidebar-nav-badge" aria-label={`${changeCount} changes`}>
              {changeCount > 999 ? '999+' : changeCount}
            </span>
          ) : item.approvalsBadge && showApprovalsBadge ? (
            <ApprovalsBadge />
          ) : null;

        if (item.comingSoon) {
          return (
            <NavLink
              key={`${item.to}-${item.label}`}
              to={`${paths.comingSoon}?feature=${encodeURIComponent(item.label)}`}
              className={`sidebar-nav-link${active ? ' active' : ''}`}
              onClick={onNavigate}
              aria-label={`${item.label} (Coming soon)`}
            >
              <Icon className="sidebar-icon" aria-hidden="true" />
              <span className="sidebar-nav-label">{item.label}</span>
              <span className="sidebar-nav-soon">Soon</span>
            </NavLink>
          );
        }

        return (
          <NavLink
            key={`${item.to}-${item.label}`}
            to={item.to}
            className={`sidebar-nav-link${active ? ' active' : ''}`}
            onClick={onNavigate}
            aria-label={item.label}
            end={item.to === paths.ib.banks}
          >
            <Icon className="sidebar-icon" aria-hidden="true" />
            <span className="sidebar-nav-label">{item.label}</span>
            {badge}
          </NavLink>
        );
      })}
    </nav>
  );
}
